/* screens/academy/CourseEditor.jsx — конструктор курса (дровер методиста).
   Вкладки: «Структура» / «Настройки» / «Ученики». Урок — дровер 2-го уровня. */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Field, TextInput, Textarea, NativeSelect, Checkbox } from '../../components/ui/Field.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import * as api from '../../lib/api/academy.js';
import { LessonEditor } from './LessonEditor.jsx';
import { StudentDetailDrawer } from './StudentDetailDrawer.jsx';
import { LandingTab } from './landing/LandingEditor.jsx';
import { TabScroller } from './TabScroller.jsx';
// Буфер обмена с фолбэком — общий с «Ссылкой на оплату» в заказах (см. модуль).
import { writeClipboard } from './clipboard.js';

const { useState, useCallback, useEffect, useRef } = React;
const { useFakeQuery } = hooks;

// Набор вкладок и права на них живут в api/academy.js (courseEditorTabs) —
// это зеркало гейтов бэкенда, и у него есть тест.

const STATUS_LABEL = { draft: 'Черновик', published: 'Опубликован', archived: 'В архиве' };
const ENR_STATUS_LABEL = { active: 'Активна', completed: 'Завершён', expired: 'Истёк' };
const ENR_STATUS_TONE = { active: 'info', completed: 'ok', expired: 'neutral' };

/* AD-8. Правка опубликованного курса меняет статусы живых записей: добавление
   обязательного урока (теста, ДЗ) возвращает всех завершивших в «Активна» и
   обнуляет completed_at. Раньше это происходило молча.
   Число завершивших и активных берём из аналитики курса — она уже их считает
   (CourseAnalyticsView), отдельного запроса под шапку заводить незачем. Запрос
   идёт ТОЛЬКО у опубликованного курса с учениками: у черновика предупреждать не
   о чем, а лишний обход всех уроков курса не бесплатен. */
function useCourseImpact(course, nonce) {
  const live = !!course && course.status === 'published' && (course.enrollment_count || 0) > 0;
  // nonce в ключе: после правки структуры число завершивших меняется, и второй
  // диалог подряд не должен называть цифру до этой правки.
  const q = useFakeQuery(
    'acad-impact-' + (course ? course.id : 0) + '-' + (live ? 1 : 0) + '-' + nonce,
    () => (live ? api.getCourseAnalytics(course.id) : Promise.resolve(null)),
    [course ? course.id : 0, live, nonce]);
  const d = q.data;
  const students = (d && d.students) || [];
  return {
    live,
    ready: !!d,
    // Запрос аналитики упал (или ещё идёт) — это НЕ «выпускников нет».
    // Раньше `completed` в обоих случаях был 0, гард молча пропускал правку, и
    // методист добавлял обязательный урок в живой курс, ничего не увидев, —
    // ровно тот молчаливый откат завершивших, ради которого AD-8 и заводилось.
    failed: !!q.error,
    active: d ? students.filter((s) => s.status === 'active').length : null,
    completed: d ? d.completed_course : null, // null = неизвестно, 0 = выпускников нет
  };
}

// Диалог перед правкой, меняющей требования курса. Возвращает true, если можно
// продолжать: у черновика и курса без выпускников — молча true.
function useRequirementGuard(impact) {
  const confirm = useConfirm();
  return useCallback(async (what) => {
    if (!impact.live) return true;              // черновик / курс без учеников
    if (impact.completed === 0) return true;    // выпускников ТОЧНО нет
    if (impact.completed == null) {             // числа нет: аналитика не ответила
      return confirm({
        title: 'Не удалось проверить, кого это затронет',
        body: api.requirementImpactUnknownPrompt(what),
        confirmLabel: 'Всё равно продолжить',
        danger: true,
      });
    }
    return confirm({
      title: 'Это затронет учеников, уже завершивших курс',
      body: api.requirementImpactPrompt(impact.completed, what),
      confirmLabel: 'Продолжить',
    });
  }, [impact.live, impact.completed, confirm]);
}

// AD-4: публичный адрес курса. Раньше slug не показывался нигде — методист выпускал
// курс и не знал, какую ссылку раздавать.
function CourseAddress({ course }) {
  const toast = useToast();
  if (!course.slug) return null;
  const path = '/course/' + course.slug;
  const url = (typeof window !== 'undefined' ? window.location.origin : '') + path;
  const copy = async () => {
    const ok = await writeClipboard(url);
    toast.show(ok ? 'Ссылка скопирована' : 'Ссылка: ' + url, ok ? 'success' : 'muted', ok ? 2500 : 6000);
  };
  return (
    <section className="acad-section">
      <SectionHead icon="link" title="Публичный адрес" />
      <div className="cluster cluster-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <code className="t-caption" style={{ wordBreak: 'break-all' }}>{path}</code>
        <Button size="sm" variant="secondary" iconLeft="copy" onClick={copy}>Копировать ссылку</Button>
        <Button size="sm" variant="ghost" iconLeft="external-link"
          onClick={() => window.open(url, '_blank', 'noopener')}>Открыть</Button>
      </div>
      <span className="t-caption text-mute">
        {course.status === 'published'
          ? 'Страница продаж курса. Ссылка открывается без авторизации.'
          : 'Курс не опубликован — по этому адресу посетитель пока увидит 404.'}
      </span>
    </section>
  );
}

function SectionHead({ icon, title, children }) {
  return (
    <div className="acad-section__head">
      <Icon name={icon} size={15} /><span className="acad-section__title">{title}</span>
      {children && <><span className="acad-section__spacer" />{children}</>}
    </div>
  );
}

// ── настройки ───────────────────────────────────────────────────────────────
function SettingsTab({ course, reload, onClose }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [form, setForm] = useState({
    title: course.title, summary: course.summary || '', description: course.description || '',
    access: course.access, price_rub: course.price_rub != null ? String(course.price_rub) : '0',
    start_on_date: course.start_on_date || '',
    status: course.status, issues_certificate: course.issues_certificate,
    slug: course.slug || '',
    cert_signer_name: course.cert_signer_name || '',
    cert_signer_title: course.cert_signer_title || '',
  });
  const [busy, setBusy] = useState(false);
  // Ошибка гейта публикации приходит DRF-полем `status` — показываем её у самого
  // селекта «Статус», а не только тостом: иначе методист жмёт «Сохранить» и читает
  // слово «Ошибка», не понимая, что публикации мешает пустой лендинг (AD-1).
  const [statusError, setStatusError] = useState('');
  // AD-4: «Такой адрес уже занят другим курсом» приходит DRF-полем `slug` —
  // показываем у поля адреса, иначе методист не поймёт, что править.
  const [slugError, setSlugError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useCallback(async () => {
    setStatusError(''); setSlugError('');
    // AD-4: slug шлём ТОЛЬКО когда методист его правда изменил. Иначе поле в теле
    // PATCH подавляло бы автогенерацию адреса из нового названия у черновика
    // (бэк: `elif 'title' in v and _slug_is_free_to_regenerate(...)`) — у
    // неопубликованного курса адрес обязан следовать за названием.
    const payload = { ...form, start_on_date: form.start_on_date || null };
    const slug = (form.slug || '').trim();
    if (slug === (course.slug || '')) delete payload.slug;
    else if (!slug) {
      // Пустой адрес бэк отклонит generic-сообщением DRF — объясняем сами.
      setSlugError('Адрес не может быть пустым — курс перестал бы открываться по ссылке.');
      return;
    } else payload.slug = slug;
    setBusy(true);
    try { await api.updateCourse(course.id, payload); toast.show('Сохранено', 'success'); reload(); }
    catch (e) {
      const msg = e?.detail || e?.message || 'Ошибка';
      if (e?.field === 'status') setStatusError(msg);
      if (e?.field === 'slug') setSlugError(msg);
      toast.show(msg, 'danger');
    } finally { setBusy(false); }
  }, [course.id, course.slug, form, toast, reload]);

  const onCover = useCallback(async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { await api.uploadCover(course.id, f); toast.show('Обложка загружена', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка загрузки', 'danger'); }
  }, [course.id, toast, reload]);

  // Отказы удаления курса, у которых `force` НЕ помогает: платёжная запись стоит
  // на PROTECT, и удовлетворить его, не удаляя саму оплату, нечем.
  const ARCHIVE_INSTEAD = ['has_purchases', 'has_tariff_enrollments', 'protected'];

  const remove = useCallback(async () => {
    if (!await confirm({ title: 'Удалить курс?', body: 'Действие необратимо.', confirmLabel: 'Удалить', danger: true })) return;
    try { await api.deleteCourse(course.id); toast.show('Курс удалён', 'muted'); onClose(true); return; }
    catch (e) {
      const code = e?.code;
      // Отказ, из которого не видно следующего шага, — тупик: раньше причина
      // печаталась в тост на 4 секунды, и «заархивируйте» приходилось делать
      // руками, найдя поле «Статус» выше по форме. Предлагаем действие.
      if (ARCHIVE_INSTEAD.includes(code)) {
        // Публикация требует включённой страницы продаж (гейт B5). У курса без
        // неё архив — дорога в одну сторону: вернуть его в каталог будет нечем,
        // пока не собрана страница. Предупреждаем ДО архивации и только в этом
        // случае — остальным курсам это лишний шум.
        const noLanding = !(course.landing_json && course.landing_json.enabled);
        const ok = await confirm({
          title: 'Удалить нельзя — заархивировать?',
          body: (e?.detail || 'По курсу есть платёжные записи.')
            + (noLanding
              ? ' Учтите: у курса не собрана страница продаж, а публикация без неё'
                + ' невозможна — из архива вернуть его в каталог получится только'
                + ' после того, как страница будет заполнена.'
              // Серверный detail уже советует архив и называет последствие —
              // повторять его своими словами значит писать одно дважды.
              : ' Данные при этом остаются на месте.'),
          confirmLabel: 'Заархивировать' });
        if (!ok) return;
        try {
          await api.updateCourse(course.id, { status: 'archived' });
          toast.show('Курс заархивирован', 'success'); onClose(true);
        } catch (e2) { toast.show(e2?.detail || e2?.message || 'Не удалось заархивировать', 'danger'); }
        return;
      }
      // Записи БЕЗ тарифов PROTECT не держит — здесь force реально работает,
      // и ветка «всё равно удалить» у методиста была только в теории:
      // `deleteCourse` умеет force, а вызов его никогда не передавал.
      if (code === 'has_enrollments') {
        const ok = await confirm({ title: 'Удалить вместе с записями учеников?',
          body: (e?.detail || 'На курс записаны ученики.')
            + ' Их записи и прогресс будут удалены. Отменить это нельзя.',
          confirmLabel: 'Удалить с записями', danger: true });
        if (!ok) return;
        try {
          await api.deleteCourse(course.id, true);
          toast.show('Курс удалён вместе с записями', 'muted'); onClose(true);
        } catch (e2) { toast.show(e2?.detail || e2?.message || 'Не удалось удалить', 'danger'); }
        return;
      }
      toast.show(e?.detail || e?.message || 'Не удалось удалить — заархивируйте курс', 'danger');
    }
  }, [course.id, confirm, toast, onClose]);

  const copy = useCallback(async () => {
    if (!await confirm({ title: 'Копировать курс?', body: 'Создастся полная копия (модули, уроки, тесты, ДЗ) как ЧЕРНОВИК. Ученики и прогресс не копируются.', confirmLabel: 'Копировать' })) return;
    setBusy(true);
    try { await api.copyCourse(course.id); toast.show('Создана копия (черновик) — откройте её в списке курсов', 'success'); onClose(true); }
    catch (e) { toast.show(e?.detail || e?.message || 'Не удалось скопировать', 'danger'); }
    finally { setBusy(false); }
  }, [course.id, confirm, toast, onClose]);

  return (
    <div className="stack stack-6">
      <section className="acad-section">
        <Field label="Название"><TextInput value={form.title} onChange={(e) => set('title', e.target.value)} /></Field>
        {/* AD-4: адрес правится вручную. После первой публикации бэк перестаёт
            перегенерировать slug из названия (чтобы не убить розданные ссылки) —
            без этого поля кривой адрес было бы нечем исправить из продукта. */}
        <Field label="Адрес (slug)" error={slugError}
          hint={`Публичная страница курса: /course/${(form.slug || '').trim() || '…'}`
            + (course.status === 'published'
              ? ' · курс опубликован — смена адреса ломает уже разосланные ссылки'
              : ' · пока курс в черновике, адрес обновляется вслед за названием')}>
          <TextInput value={form.slug} invalid={!!slugError}
            onChange={(e) => { set('slug', e.target.value); if (slugError) setSlugError(''); }}
            placeholder="povarskoy-minimum" />
        </Field>
        <Field label="Краткое описание (для карточки)"><TextInput value={form.summary} onChange={(e) => set('summary', e.target.value)} /></Field>
        <Field label="Описание (Markdown)"><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} minRows={3} /></Field>
        <div className="acad-grid-form">
          <Field label="Доступ"><NativeSelect value={form.access} onChange={(e) => set('access', e.target.value)}
            options={[
              { value: 'invite', label: 'По записи методистом' },
              { value: 'open', label: 'Открытый бесплатный (самозапись)' },
              { value: 'paid', label: 'Открытый платный (покупка)' },
            ]} /></Field>
          <Field label="Статус" error={statusError}>
            <NativeSelect value={form.status} invalid={!!statusError} onChange={(e) => set('status', e.target.value)}
              options={[{ value: 'draft', label: 'Черновик' }, { value: 'published', label: 'Опубликован' }, { value: 'archived', label: 'В архиве' }]} /></Field>
        </div>
        {form.access === 'paid' && (
          <div style={{ maxWidth: 220 }}>
            <Field label="Цена, ₽" hint="разовая покупка · постоянный доступ">
              <input className="input" type="number" min="1" step="1" value={form.price_rub}
                onChange={(e) => set('price_rub', e.target.value)} placeholder="например, 1990" />
            </Field>
          </div>
        )}
        <div style={{ maxWidth: 260 }}>
          <Field label="Дата старта потока" hint="уроки откроются не раньше этой даты · пусто — сразу после записи">
            <input className="input" type="date" value={form.start_on_date || ''}
              onChange={(e) => set('start_on_date', e.target.value)} />
          </Field>
        </div>
        <Checkbox label="Выдавать сертификат по завершении курса" checked={form.issues_certificate}
          onChange={(e) => set('issues_certificate', e.target.checked)} />
        {/* Подписант курса. Поля жили в модели и читались при выдаче сертификата,
            но задать их из продукта было нельзя — курс приглашённого преподавателя
            всегда подписывала платформа. Показываем только когда сертификат выдаётся:
            иначе это настройка документа, которого не будет. */}
        {form.issues_certificate && (
          <div className="acad-grid-form">
            <Field label="Подпись в сертификате (ФИО)"
              hint="пусто — подписывает платформа">
              <TextInput value={form.cert_signer_name}
                onChange={(e) => set('cert_signer_name', e.target.value)} />
            </Field>
            <Field label="Должность подписавшего" hint="пусто — как у платформы">
              <TextInput value={form.cert_signer_title}
                onChange={(e) => set('cert_signer_title', e.target.value)} />
            </Field>
          </div>
        )}
        <div><Button variant="primary" loading={busy} onClick={save}>Сохранить</Button></div>
      </section>

      <CourseAddress course={course} />

      <section className="acad-section">
        <SectionHead icon="image" title="Обложка" />
        {course.cover_url ? (
          <div className="acad-mediacard">
            <img src={course.cover_url} alt="" />
            <label className="acad-uploader"><Icon name="upload" size={16} /><span>Заменить обложку</span>
              <input type="file" accept="image/*" onChange={onCover} /></label>
          </div>
        ) : (
          <label className="acad-uploader"><Icon name="upload" size={18} />
            <div><div>Загрузить обложку</div><div className="acad-uploader__hint">JPG / PNG, 16:9</div></div>
            <input type="file" accept="image/*" onChange={onCover} /></label>
        )}
      </section>

      <section className="acad-section">
        {/* Нейтральная раскладка: копирование ничего не разрушает. Раньше здесь
            стоял `set-card__danger` ради флекса, и безобидное действие получало
            розовый фон с красным заголовком в двух сантиметрах от «Опасной зоны». */}
        <div className="set-card__row">
          <div className="stack stack-2"><span className="t-label">Копировать курс</span>
            <span className="t-caption">Полная копия (модули, уроки, тесты, ДЗ) как черновик. Удобно для нового потока или похожего тренинга.</span></div>
          <Button variant="secondary" iconLeft="copy" loading={busy} onClick={copy}>Копировать</Button>
        </div>
      </section>

      <section className="acad-section">
        <SectionHead icon="trash" title="Опасная зона" />
        <div className="set-card__danger">
          <div className="stack stack-2"><span className="t-label">Удалить курс</span>
            <span className="t-caption">Действие необратимо. Если есть ученики — заархивируйте курс.</span></div>
          <Button variant="danger" iconLeft="trash" onClick={remove}>Удалить</Button>
        </div>
      </section>
    </div>
  );
}

// ── структура ───────────────────────────────────────────────────────────────
function ModuleBlock({ module, courseId, index, total, onMoveModule, reload, openLesson, guard }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [title, setTitle] = useState(module.title);
  // AD-12: описание модуля бэк принимает (ModuleWriteSerializer.summary) и отдаёт,
  // а поля в UI не было — методист не мог объяснить ученику, о чём блок.
  const [summary, setSummary] = useState(module.summary || '');
  const [days, setDays] = useState(module.unlock_after_days || 0);
  // #2: перенос модуля в другой курс — ленивая загрузка списка курсов при открытии
  const [moveOpen, setMoveOpen] = useState(false);
  const [courses, setCourses] = useState(null);
  const [moveTo, setMoveTo] = useState('');
  const [moving, setMoving] = useState(false);
  const lessons = module.lessons || [];

  const saveModule = async () => {
    try {
      await api.updateModule(module.id,
        { title, summary, unlock_after_days: Number(days) || 0 });
      toast.show('Модуль сохранён', 'success'); reload();
    } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  // AD-3: бэк отклоняет удаление контента с работами учеников кодом has_student_data
  // и ждёт повтора с force=1 — раньше методисту показывали инструкцию про ?force=1,
  // которую из UI выполнить было нечем.
  const delModule = async () => {
    if (!await confirm({ title: 'Удалить модуль?', body: 'Вместе с уроками.', confirmLabel: 'Удалить', danger: true })) return;
    try { await api.deleteModule(module.id); reload(); return; }
    catch (e) {
      if (!api.isStudentDataGuard(e)) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); return; }
      const ok = await confirm({ title: 'Удалить вместе с работами учеников?',
        body: api.studentDataPrompt(e, 'этому модулю'),
        confirmLabel: 'Удалить с результатами', danger: true });
      if (!ok) return;
      try { await api.deleteModule(module.id, true); toast.show('Модуль удалён вместе с работами', 'muted'); reload(); }
      catch (e2) { toast.show(e2?.detail || e2?.message || 'Ошибка', 'danger'); }
    }
  };
  const dupModule = async () => {
    try { await api.copyModule(module.id); toast.show('Модуль продублирован', 'success'); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  const dupLesson = async (lessonId) => {
    try { await api.copyLesson(lessonId, module.id); toast.show('Урок продублирован', 'success'); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  // AD-2: порядок уроков задаётся кнопками «выше/ниже» — он определяет последовательные
  // локи ученика, поэтому «вставить урок в середину» обязано быть возможно.
  const moveLessonBy = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= lessons.length) return;
    const ids = lessons.map((l) => l.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await api.reorderLessons(module.id, ids); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Не удалось изменить порядок', 'danger'); }
  };
  const openMove = async () => {
    setMoveOpen((v) => !v);
    if (courses == null) {
      try { setCourses(await api.listCourses()); }
      catch (e) { toast.show(e?.detail || e?.message || 'Не удалось загрузить курсы', 'danger'); setCourses([]); }
    }
  };
  const doMove = async () => {
    if (!moveTo) return;
    if (!await confirm({ title: 'Перенести модуль в другой курс?', body: 'Модуль со всеми уроками переедет в выбранный курс. Прогресс учеников обоих курсов пересчитается.', confirmLabel: 'Перенести' })) return;
    setMoving(true);
    try { await api.moveModule(module.id, Number(moveTo)); toast.show('Модуль перенесён', 'success'); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Не удалось перенести', 'danger'); }
    finally { setMoving(false); }
  };
  // AD-8: новый урок создаётся ОБЯЗАТЕЛЬНЫМ, а значит меняет требования курса и
  // откатывает выпускников в «Активна». Спрашиваем до создания, а не после.
  const addLesson = async () => {
    if (!await guard('в курс добавится обязательный урок')) return;
    try { const l = await api.createLesson(module.id, { title: 'Новый урок' }); reload(); openLesson(l.id); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };

  // курсы для переноса: все, кроме текущего
  const moveOptions = [{ value: '', label: '— выберите курс —' },
    ...(courses || []).filter((c) => c.id !== courseId).map((c) => ({ value: String(c.id), label: c.title }))];

  return (
    <div className="acad-module">
      <div className="acad-module__head">
        {/* Порядок — кнопками (AD-2). Ручка acad-grip раньше обещала перетаскивание,
            которого нет ни мышью, ни клавиатурой.
            Кнопки без подписи объявляются кит-пропом iconOnly: iconLeft без
            children даёт btn без класса btn--icon, т.е. текстовые паддинги вокруг
            пустого места и сбитую сетку строки. */}
        <span className="cluster cluster-2">
          <Button size="sm" variant="ghost" iconOnly="chevron-up" disabled={index === 0}
            onClick={() => onMoveModule(-1)} aria-label={`Переместить модуль «${module.title}» выше`}
            title="Выше" />
          <Button size="sm" variant="ghost" iconOnly="chevron-down" disabled={index === total - 1}
            onClick={() => onMoveModule(1)} aria-label={`Переместить модуль «${module.title}» ниже`}
            title="Ниже" />
        </span>
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} className="grow" />
        <div className="acad-module__days">
          <Field label="Открыть через, дн" hint="после записи · 0 — сразу">
            <input className="input" type="number" min="0" value={days}
              title="Через сколько дней после записи студента на курс открыть этот модуль. 0 — доступен сразу."
              onChange={(e) => setDays(e.target.value)} />
          </Field>
        </div>
        <Button size="sm" variant="secondary" onClick={saveModule}>Сохранить</Button>
        <Button size="sm" variant="ghost" iconOnly="copy" onClick={dupModule} aria-label="Дублировать модуль" title="Дублировать модуль" />
        <Button size="sm" variant={moveOpen ? 'secondary' : 'ghost'} iconOnly="arrow-up-right" onClick={openMove}
          aria-label="Перенести в курс" title="Перенести в другой курс" />
        <Button size="sm" variant="ghost" iconOnly="trash" onClick={delModule} aria-label="Удалить модуль" title="Удалить модуль" />
      </div>
      <div className="acad-module__meta">
        <Field label="Описание модуля" hint="видит ученик в программе курса · сохраняется кнопкой «Сохранить»">
          <TextInput value={summary} onChange={(e) => setSummary(e.target.value)}
            placeholder="О чём этот блок и что ученик научится делать" />
        </Field>
      </div>
      {moveOpen && (
        <div className="acad-module__move cluster cluster-8" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Перенести в курс…" className="grow">
            <NativeSelect value={moveTo} onChange={(e) => setMoveTo(e.target.value)}
              options={moveOptions} disabled={courses == null} />
          </Field>
          <Button size="sm" variant="secondary" disabled={!moveTo} loading={moving} onClick={doMove}>Перенести</Button>
        </div>
      )}
      <div className="acad-module__body">
        {lessons.map((l, i) => (
          <div key={l.id} className="acad-lesson-row">
            <span className="cluster cluster-2">
              <Button size="sm" variant="ghost" iconOnly="chevron-up" disabled={i === 0}
                onClick={() => moveLessonBy(i, -1)} aria-label={`Переместить урок «${l.title}» выше`}
                title="Выше" />
              <Button size="sm" variant="ghost" iconOnly="chevron-down" disabled={i === lessons.length - 1}
                onClick={() => moveLessonBy(i, 1)} aria-label={`Переместить урок «${l.title}» ниже`}
                title="Ниже" />
            </span>
            {/* Порядок уроков определяет последовательные локи ученика, поэтому
                двигать их вслепую нельзя: НАЗВАНИЕ приоритетнее бейджей. Внешняя
                строка .acad-lesson-row переносится в узкой панели, но эта кнопка —
                один её flex-элемент и внутри не переносилась: бейджи (flex 0 0 auto)
                съедали ширину, и на 380 от названия оставалось три символа.
                flexWrap + пол ширины у названия: когда бейджи рядом не помещаются,
                они уезжают на вторую строку, а название занимает всю первую. */}
            <button type="button" onClick={() => openLesson(l.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flex: '1 1 auto',
                minWidth: 0, padding: 0, background: 'none', border: 0, cursor: 'pointer',
                textAlign: 'left', color: 'inherit', flexWrap: 'wrap', rowGap: 'var(--space-4)' }}>
              <span className="acad-lesson-row__title" title={l.title}
                style={{ minWidth: '14ch' }}>{l.title}</span>
              <span className="acad-lesson-row__tags">
                {l.has_video && <span className="acad-type-badge acad-type-badge--video"><Icon name="camera" />Видео</span>}
                {l.has_quiz && <span className="acad-type-badge acad-type-badge--quiz"><Icon name="check" />Тест</span>}
                {l.has_homework && <span className="acad-type-badge acad-type-badge--hw"><Icon name="edit" />ДЗ</span>}
                {l.is_bonus && <span className="acad-type-badge acad-type-badge--bonus">бонус</span>}
              </span>
              <Icon name="chevron-right" size={16} className="text-mute" />
            </button>
            <Button size="sm" variant="ghost" iconOnly="copy" onClick={() => dupLesson(l.id)}
              aria-label="Дублировать урок" title="Дублировать урок" />
          </div>
        ))}
        <div><Button size="sm" variant="ghost" iconLeft="plus" onClick={addLesson}>Урок</Button></div>
      </div>
    </div>
  );
}

function StructureTab({ course, reload, openLesson, guard }) {
  const toast = useToast();
  const modules = course.modules || [];
  const addModule = async () => {
    try { await api.createModule(course.id, { title: 'Новый модуль' }); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  // AD-2: порядок модулей — существующий эндпоинт reorder, которого UI не звал ни разу.
  const moveModule = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= modules.length) return;
    const ids = modules.map((m) => m.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await api.reorderModules(course.id, ids); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Не удалось изменить порядок', 'danger'); }
  };
  return (
    <div className="stack stack-4">
      {modules.length === 0 && (
        <div className="acad-block-empty">
          <Icon name="layers" size={28} />
          <div className="acad-block-empty__title">Пока нет модулей</div>
          <span className="t-caption">Добавьте первый модуль курса.</span>
        </div>
      )}
      {modules.map((m, i) => (
        <ModuleBlock key={m.id} module={m} courseId={course.id} index={i} total={modules.length}
          onMoveModule={(dir) => moveModule(i, dir)} reload={reload} openLesson={openLesson}
          guard={guard} />
      ))}
      <div><Button variant="secondary" iconLeft="plus" onClick={addModule}>Добавить модуль</Button></div>
    </div>
  );
}

/* AD-6. Массовая запись десятью адресами заканчивалась ОДНИМ тостом «Записано: 3»
   — какие семь не прошли и почему, методист не узнавал ниоткуда и записывал
   вслепую заново. Панель держит разбор по адресам и не уезжает по таймеру:
   именно список непрошедших и нужен, чтобы их поправить.
   Каждая группа раскрывается — иначе десять адресов «Записано» выталкивают с
   экрана три строки «Ошибок», ради которых панель и заводилась. */
function EnrollResult({ result, onDismiss }) {
  const groups = [
    { key: 'enrolled', label: 'Записано', tone: 'ok', items: result.enrolled, count: result.counts.enrolled },
    { key: 'invited', label: 'Приглашено', tone: 'info', items: result.invited, count: result.counts.invited },
    { key: 'failed', label: 'Ошибок', tone: 'bad', items: result.failed, count: result.counts.failed },
  ];
  // Ошибки раскрыты сразу: это единственная группа, требующая действия.
  const [open, setOpen] = useState(result.counts.failed ? 'failed' : '');
  const shown = groups.find((g) => g.key === open && g.items.length);
  return (
    <div className="acad-enroll-result" role="status">
      <div className="acad-enroll-result__head">
        {groups.map((g) => (
          <button key={g.key} type="button" disabled={!g.items.length}
            aria-expanded={g.items.length ? open === g.key : undefined}
            className={'acad-enroll-chip acad-enroll-chip--' + g.tone
              + (open === g.key && g.items.length ? ' is-open' : '')}
            title={g.items.length ? 'Показать адреса' : 'Адресов в этой группе нет'}
            onClick={() => setOpen((v) => (v === g.key ? '' : g.key))}>
            <span>{g.label}: <span className="num">{g.count}</span></span>
            {!!g.items.length && <Icon name={open === g.key ? 'chevron-up' : 'chevron-down'} size={12} />}
          </button>
        ))}
        <span className="acad-section__spacer" />
        <Button size="sm" variant="ghost" iconOnly="close" onClick={onDismiss}
          aria-label="Скрыть итог записи" title="Скрыть" />
      </div>
      {/* Старый ответ бэка — только счётчик. Молча показать пустые списки значило
          бы соврать, что не прошедших нет. */}
      {!result.detailed && (
        <span className="t-caption text-mute">
          Разбор по адресам сервер не прислал — известно только число записанных.
        </span>
      )}
      {shown && (
        <ul className="acad-enroll-list">
          {shown.items.map((it, i) => (
            <li key={it.email + '-' + i} className="acad-enroll-list__row">
              <span className="acad-enroll-list__mail">{it.email}</span>
              {shown.key === 'failed' && (
                <span className="acad-enroll-list__why">{api.enrollFailureText(it.reason)}</span>
              )}
              {/* Уже записанные попадают в «Записано» — иначе методист не поймёт,
                  почему список учеников не вырос на десять человек. */}
              {shown.key === 'enrolled' && it.already && (
                <span className="t-caption text-mute">был записан раньше</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── ученики ─────────────────────────────────────────────────────────────────
function StudentsTab({ course, reloadCourse }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [nonce, setNonce] = useState(0);
  const [emails, setEmails] = useState('');
  // AD-6: разбор последней массовой записи по адресам; null — записи ещё не было.
  const [enrollResult, setEnrollResult] = useState(null);
  const [filterTariff, setFilterTariff] = useState(''); // '' = все, 'none' = без тарифа, id = тариф (#4)
  const [detailId, setDetailId] = useState(null); // открытая карточка ученика (#13)
  // поиск по ученикам (B3): debounce ~300мс перед запросом ?q=
  const [search, setSearch] = useState('');
  const [searchQ, setSearchQ] = useState(''); // отложенное (debounced) значение, уходит в запрос
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const tariffs = course.tariffs || [];
  const [assignTariff, setAssignTariff] = useState('');
  const tariffOptions = [{ value: '', label: 'Без тарифа (полный доступ)' },
    ...tariffs.map((t) => ({ value: String(t.id), label: t.title }))];
  // фильтр над списком: «Все» / «Без тарифа» / конкретные тарифы (#4)
  const filterOptions = [{ value: '', label: 'Все тарифы' }, { value: 'none', label: 'Без тарифа' },
    ...tariffs.map((t) => ({ value: String(t.id), label: t.title }))];
  // tariffId в запрос: '' → не шлём (все), 'none' → строка, id → число (academy.js #4)
  const filterArg = filterTariff === '' ? {}
    : { tariffId: filterTariff === 'none' ? 'none' : Number(filterTariff) };
  const q = useFakeQuery('academy-enr-' + course.id + '-' + filterTariff + '-' + searchQ + '-' + nonce,
    () => api.listEnrollments(course.id, { ...filterArg, q: searchQ }), [course.id, filterTariff, searchQ, nonce]);
  const items = q.data || []; // ПЛОСКИЙ массив записей (не data.results)
  /* Локальный reload перечитывает только СПИСОК записей. Шапка дровера
     («N учеников», полоса «Опубликован · N активных») живёт в дереве курса,
     которое грузит родитель, — поэтому сразу после массовой записи заголовок
     продолжал показывать «0 учеников». Дёргаем оба. */
  const reload = () => { setNonce((n) => n + 1); if (reloadCourse) reloadCourse(); };

  const add = async () => {
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setEnrollResult(null);
    try {
      const r = await api.enrollUsers(course.id,
        { emails: list, ...(assignTariff ? { tariff_id: Number(assignTariff) } : {}) });
      // AD-6: тост несёт только сводку, поимённый разбор живёт в панели ниже —
      // тост уезжает через секунды, а список непрошедших адресов нужен дольше.
      const parsed = api.normalizeEnrollResult(r);
      setEnrollResult(parsed);
      toast.show(api.enrollSummaryText(parsed), parsed.counts.failed ? 'warning' : 'success');
      setEmails(''); reload();
    } catch (e) { toast.show(e?.detail || e?.message || 'Не нашли пользователей', 'danger'); }
  };
  const changeTariff = async (id, tariffId) => {
    try { await api.patchEnrollment(id, { tariff_id: tariffId === '' ? null : Number(tariffId) });
      toast.show('Тариф ученика обновлён', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка', 'danger'); }
  };
  // Отписка — единственное разрушительное действие методиста, которое раньше шло
  // без гарда и уносило каскадом сдачи, журнал проверок и выданный сертификат.
  // Схема та же, что у удаления контента: сначала чистый запрос, на гард —
  // подтверждение и повтор с force. Сертификат force не пропускает: у отказа
  // `certificate_issued` выход другой (отозвать в карточке ученика), и второе
  // подтверждение там было бы кнопкой, которая ничего не делает.
  const remove = async (id) => {
    if (!await confirm({ title: 'Отчислить ученика?', confirmLabel: 'Отчислить', danger: true })) return;
    try { await api.deleteEnrollment(id); reload(); return; }
    catch (e) {
      if (!api.isStudentDataGuard(e)) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); return; }
      const ok = await confirm({ title: 'Отчислить вместе с работами?',
        body: api.studentDataPrompt(e, 'этому курсу'),
        confirmLabel: 'Отчислить с результатами', danger: true });
      if (!ok) return;
      try { await api.deleteEnrollment(id, true); toast.show('Ученик отчислен вместе с работами', 'muted'); reload(); }
      catch (e2) { toast.show(e2?.detail || e2?.message || 'Ошибка', 'danger'); }
    }
  };
  const toggleFreeze = async (e) => {
    try { await api.patchEnrollment(e.id, { action: e.frozen_at ? 'unfreeze' : 'freeze' });
      toast.show(e.frozen_at ? 'Доступ возобновлён' : 'Доступ заморожен', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка', 'danger'); }
  };
  const setExpiry = async (id, date) => {
    try { await api.patchEnrollment(id, { expires_at: date || null });
      toast.show('Срок доступа обновлён', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка', 'danger'); }
  };

  return (
    <>
    <div className="stack stack-4">
      <section className="acad-section">
        <Field label="Записать по email (через запятую или с новой строки)">
          <Textarea value={emails} onChange={(e) => setEmails(e.target.value)} minRows={2}
            placeholder="anna@example.com, ivan@example.com" />
        </Field>
        {tariffs.length > 0 && (
          <Field label="Тариф для записываемых" hint="окна доступа и набор модулей по тарифу">
            <NativeSelect value={assignTariff} onChange={(e) => setAssignTariff(e.target.value)}
              options={tariffOptions} />
          </Field>
        )}
        <div className="cluster cluster-8">
          <Button variant="secondary" iconLeft="plus" onClick={add}>Записать</Button>
          {items.length > 0 && (
            <Button variant="ghost" iconLeft="download"
              onClick={() => window.open(api.enrollmentsExportUrl(course.id), '_blank')}>
              Выгрузить CSV
            </Button>
          )}
        </div>
        {enrollResult && (
          // key по составу итога: следующая запись с ошибками обязана раскрыть
          // «Ошибок» заново, а не унаследовать свёрнутое состояние прошлой.
          <EnrollResult key={Object.values(enrollResult.counts).join('-')}
            result={enrollResult} onDismiss={() => setEnrollResult(null)} />
        )}
      </section>
      <div className="cluster cluster-8" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Поиск ученика" className="grow">
          <TextInput value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по email или ФИО" />
        </Field>
        {tariffs.length > 0 && (
          <div style={{ maxWidth: 280 }}>
            <Field label="Фильтр по тарифу">
              <NativeSelect value={filterTariff} onChange={(e) => setFilterTariff(e.target.value)}
                options={filterOptions} />
            </Field>
          </div>
        )}
      </div>
      <QueryBoundary loading={q.loading} error={q.error} empty={!items.length} onRetry={reload}
        emptyEl={searchQ
          ? (<div className="acad-block-empty"><Icon name="user" size={28} />
              <div className="acad-block-empty__title">Никого не нашлось</div>
              <span className="t-caption">Измените запрос поиска.</span></div>)
          : (<div className="acad-block-empty"><Icon name="user" size={28} />
              <div className="acad-block-empty__title">{filterTariff ? 'Нет учеников по фильтру' : 'Ещё нет учеников'}</div>
              <span className="t-caption">{filterTariff ? 'Сбросьте фильтр или выберите другой тариф.' : 'Запишите учеников по email выше.'}</span></div>)}>
        <div className="stack stack-2">
          {items.map((e) => (
            <div key={e.id} className="acad-student-row">
              <button type="button" className="acad-student-row__open" onClick={() => setDetailId(e.id)}
                title="Открыть карточку ученика">
                <Avatar label={e.user_label} size={32} />
                <span className="acad-student-row__main">
                  <span className="acad-student-row__name">{e.user_label}</span>
                  <span className="acad-student-row__email">{e.user_email}</span>
                </span>
              </button>
              {/* Пауза подписки — состояние, которого нет ни в статусе, ни в
                  frozen_at: методист видел «активен, тариф такой-то» у ученика,
                  у которого заперт весь курс (и часто сам же его туда отправил,
                  сняв план с тарифа). Заморозку не дублируем — она видна
                  кнопкой «Разморозить» справа. */}
              {e.tariff?.subscription_hold && !e.frozen_at && (
                <Pill tone="accent" size="sm" glyph={false} title="Доступ выдан подпиской, которая сейчас не активна">
                  На паузе
                </Pill>
              )}
              {e.status === 'completed'
                ? <Pill tone="ok" size="sm" glyph={false}>Завершён</Pill>
                : <span className="acad-progress">
                    <span className="acad-progress__bar"><span className="acad-progress__fill" style={{ width: e.progress_pct + '%' }} /></span>
                    <span className="t-caption num">{e.progress_pct}%</span>
                  </span>}
              {tariffs.length > 0 && (
                <span className="acad-access" title="Тариф ученика">
                  <NativeSelect value={e.tariff ? String(e.tariff.id) : ''}
                    onChange={(ev) => changeTariff(e.id, ev.target.value)} options={tariffOptions} />
                </span>
              )}
              <span className="acad-access" title="Доступ до (пусто — бессрочно)">
                <input type="date" className="input acad-access__date"
                  value={e.expires_at ? e.expires_at.slice(0, 10) : ''}
                  onChange={(ev) => setExpiry(e.id, ev.target.value)} />
                <Button size="sm" variant={e.frozen_at ? 'secondary' : 'ghost'} iconLeft="clock"
                  onClick={() => toggleFreeze(e)}>{e.frozen_at ? 'Разморозить' : 'Заморозить'}</Button>
              </span>
              <Button size="sm" variant="ghost" iconOnly="trash" onClick={() => remove(e.id)}
                aria-label="Отчислить" title="Отчислить" />
            </div>
          ))}
        </div>
      </QueryBoundary>
    </div>
    {detailId && <StudentDetailDrawer enrollmentId={detailId} onClose={() => setDetailId(null)} />}
    </>
  );
}

function AnalyticsTab({ course }) {
  const q = useFakeQuery('academy-analytics-' + course.id, () => api.getCourseAnalytics(course.id), [course.id]);
  const d = q.data;
  // CU-6: клик по фамилии открывает карточку ученика. Раньше аналитика давала три
  // агрегата — назвать отвалившегося и открыть его прогресс было нечем.
  const [detailId, setDetailId] = useState(null);
  const students = (d && d.students) || [];
  return (
    <QueryBoundary loading={q.loading} error={q.error} onRetry={q.reload}>
      {d && (
        <div className="stack stack-6">
          <div className="acad-analytics__kpis">
            <div className="acad-kpi"><div className="acad-kpi__val num">{d.total_students}</div><div className="acad-kpi__lbl">Учеников</div></div>
            <div className="acad-kpi"><div className="acad-kpi__val num">{d.completed_course}</div><div className="acad-kpi__lbl">Завершили курс</div></div>
            <div className="acad-kpi"><div className="acad-kpi__val num">{d.avg_progress}%</div><div className="acad-kpi__lbl">Средний прогресс</div></div>
          </div>
          {/* AD-15: заявки с публичной страницы продаж этого курса. Раньше форма
              лендинга уводила заявку в CRM, и в Академии её след пропадал: сколько
              людей написало и сколько из них заплатило, методист не знал. */}
          <section className="acad-section">
            <SectionHead icon="paper-plane" title="Заявки с лендинга" />
            <span className="t-label">
              Заявок: <span className="num">{d.leads_total ?? 0}</span>
              {' · '}Куплено: <span className="num">{d.leads_purchased ?? 0}</span>
            </span>
            <span className="t-caption text-mute">
              Заявки формы страницы продаж курса. «Куплено» — те, кто после заявки
              оплатил курс с той же почты; заявки видны в CRM.
            </span>
          </section>
          <section className="acad-section">
            <div className="acad-section__head"><Icon name="check-circle" size={15} /><span className="acad-section__title">Прохождение по урокам</span></div>
            {d.lessons.length === 0
              ? <span className="t-caption text-mute">В курсе ещё нет уроков.</span>
              : d.lessons.map((l) => (
                <div key={l.id} className="acad-lessonbar">
                  <span className="acad-lessonbar__name" title={l.title}>{l.title}{l.is_bonus && <span className="t-caption text-mute"> · бонус</span>}</span>
                  <span className="acad-lessonbar__track"><span className="acad-lessonbar__fill" style={{ width: l.rate_pct + '%' }} /></span>
                  <span className="acad-lessonbar__pct">{l.completed}/{d.total_students} · {l.rate_pct}%</span>
                </div>
              ))}
          </section>
          <section className="acad-section">
            <SectionHead icon="user" title="Ученики курса">
              <span className="acad-section__hint">по убыванию прогресса</span>
            </SectionHead>
            {students.length === 0
              ? <span className="t-caption text-mute">На курс ещё никто не записан.</span>
              : (
                <div className="stack stack-2">
                  {students.map((s) => (
                    <button type="button" key={s.id} className="acad-anrow"
                      onClick={() => setDetailId(s.id)}
                      title={`Открыть карточку ученика ${s.name}`}>
                      <Avatar label={s.name} size={28} />
                      <span className="acad-anrow__name">{s.name}</span>
                      <span className="acad-progress">
                        <span className="acad-progress__bar">
                          <span className={'acad-progress__fill' + (s.status === 'completed' ? ' is-done' : '')}
                            style={{ width: (s.progress_pct || 0) + '%' }} />
                        </span>
                        <span className="t-caption num">{s.progress_pct || 0}%</span>
                      </span>
                      <Pill tone={ENR_STATUS_TONE[s.status] || 'neutral'} size="sm" glyph={false}>
                        {ENR_STATUS_LABEL[s.status] || s.status}</Pill>
                    </button>
                  ))}
                </div>
              )}
          </section>
          {detailId && <StudentDetailDrawer enrollmentId={detailId} onClose={() => setDetailId(null)} />}
        </div>
      )}
    </QueryBoundary>
  );
}

// ── тарифы (B1) ─────────────────────────────────────────────────────────────
// режим приёма ДЗ (#1) — три варианта, привязка к homework_review_mode
const HW_MODES = [
  { value: 'curator', label: 'Проверяет куратор' },
  { value: 'auto', label: 'Автоприём (без проверки)' },
  { value: 'auto_after_feedback', label: 'Куратор пока идёт ОС, затем автоприём' },
];
// деривация режима из контракта: явный homework_review_mode, иначе из legacy bool
// Зеркалит Tariff.GRANT_AUDIENCE_CHOICES / GRANT_REACH_CHOICES бэкенда.
const GRANT_AUDIENCES = [
  { value: '', label: 'Только выбранные планы' },
  { value: 'personal', label: 'Все личные планы (B2C)' },
  { value: 'business', label: 'Все бизнес-планы (B2B)' },
  { value: 'any', label: 'Любой платный план' },
];
const GRANT_REACHES = [
  { value: 'owner', label: 'Только владелец пространства' },
  { value: 'team', label: 'Вся команда пространства' },
];

function deriveHwMode(tariff) {
  if (tariff.homework_review_mode) return tariff.homework_review_mode;
  return tariff.homework_auto_accept ? 'auto' : 'curator';
}

function TariffCard({ tariff, modules, plans = [], reload }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [t, setT] = useState({
    title: tariff.title, price_rub: String(tariff.price_rub ?? 0),
    homework_review_mode: deriveHwMode(tariff),
    includes_all_modules: tariff.includes_all_modules,
    grants_certificate: tariff.grants_certificate !== false,
    access_days: tariff.access_days ?? '', feedback_days: tariff.feedback_days ?? '',
    is_active: tariff.is_active, module_ids: tariff.module_ids || [],
    // !== false — тариф, заведённый до появления полей, приезжает без них и
    // обязан читаться как «включено» (дефолт модели), а не как выключенное.
    homework_enabled: tariff.homework_enabled !== false,
    show_locked_modules: tariff.show_locked_modules !== false,
    grant_by_subscription: !!tariff.grant_by_subscription,
    granted_to_audience: tariff.granted_to_audience || '',
    grant_reach: tariff.grant_reach || 'owner',
    granted_plans: tariff.granted_plans || [],
  });
  const [busy, setBusy] = useState(false);
  // Ошибки валидации тарифа (напр. «активный тариф не может стоить 0») показываем
  // у самого поля: тост уезжает, а методист остаётся с непонятно почему не сохранённой
  // формой. Ключ '_' — ошибка без привязки к полю (non_field_errors).
  const [fieldErr, setFieldErr] = useState({});
  const set = (k, v) => setT((s) => ({ ...s, [k]: v }));
  const toggleModule = (id) => setT((s) => ({ ...s,
    module_ids: s.module_ids.includes(id) ? s.module_ids.filter((x) => x !== id) : [...s.module_ids, id] }));
  // Память охватов ПЕРЕЖИВАЕТ снятие галок. Без неё «Убрать все» стирал
  // настроенные охваты, и следующее «Выбрать все» возвращало планы уже с
  // дефолтным «владелец» — методист терял полтора десятка решений одним кликом,
  // причём молча.
  const reachMemo = useRef({});
  (tariff.granted_plans || []).forEach((r) => {
    if (reachMemo.current[r.slug] === undefined) reachMemo.current[r.slug] = r.reach;
  });
  const rememberedReach = (slug) => reachMemo.current[slug] || 'owner';
  const planReach = (slug) => (t.granted_plans.find((r) => r.slug === slug) || {}).reach;
  const togglePlan = (slug) => setT((s) => ({ ...s,
    granted_plans: s.granted_plans.some((r) => r.slug === slug)
      ? s.granted_plans.filter((r) => r.slug !== slug)
      : [...s.granted_plans, { slug, reach: rememberedReach(slug) }] }));
  const setPlanReach = (slug, reach) => {
    reachMemo.current[slug] = reach;
    setT((s) => ({ ...s,
      granted_plans: s.granted_plans.map((r) => (r.slug === slug ? { ...r, reach } : r)) }));
  };
  const allPlans = () => setT((s) => ({ ...s,
    granted_plans: plans.map((p) => ({
      slug: p.slug,
      reach: (s.granted_plans.find((r) => r.slug === p.slug) || {}).reach
        || rememberedReach(p.slug),
    })) }));
  const noPlans = () => setT((s) => ({ ...s, granted_plans: [] }));
  // Включили выдачу — все планы отмечены сразу: снять лишние быстрее, чем
  // отметить полтора десятка. Уже настроенный тариф не трогаем.
  const toggleGrant = (on) => setT((s) => ({ ...s,
    grant_by_subscription: on,
    granted_plans: on && !s.granted_plans.length
      ? plans.map((p) => ({ slug: p.slug, reach: rememberedReach(p.slug) }))
      : s.granted_plans }));

  const save = async () => {
    setBusy(true); setFieldErr({});
    try {
      const saved = await api.updateTariff(tariff.id, {
        title: t.title, price_rub: t.price_rub, homework_review_mode: t.homework_review_mode,
        includes_all_modules: t.includes_all_modules,
        grants_certificate: t.grants_certificate,
        access_days: t.access_days === '' ? null : Number(t.access_days),
        feedback_days: t.feedback_days === '' ? null : Number(t.feedback_days),
        is_active: t.is_active, module_ids: t.module_ids,
        homework_enabled: t.homework_enabled,
        show_locked_modules: t.show_locked_modules,
        grant_by_subscription: t.grant_by_subscription,
        granted_to_audience: t.granted_to_audience,
        grant_reach: t.grant_reach,
        // Поимённые планы шлём только в своём режиме: в аудиторном они не
        // читаются, и отправлять их значит молча копить мусор в БД.
        granted_plans: t.granted_to_audience ? [] : t.granted_plans,
      });
      // Правка условий выдачи — действие с ПОБОЧНЫМ ЭФФЕКТОМ на живых людях:
      // снятие плана ставит их доступ на паузу, добавление — открывает курс.
      // Молчаливое «Тариф сохранён» уже стоило инцидента: методист снял планы,
      // восемь учеников встали на паузу, и узнал он об этом от них. Поэтому
      // масштаб называем числом и в тот же момент. Провал пересчёта отличаем от
      // «пересчёта не было» (бэк отдаёт ok) — иначе непринятая выдача выглядела
      // бы как успешное сохранение.
      const gs = saved && saved.grants_sync;
      if (gs && gs.ok === false) {
        toast.show('Тариф сохранён, но пересчёт доступов не удался — проверьте список учеников',
          'danger', 8000);
      } else if (gs && (gs.granted || gs.held)) {
        const parts = [];
        if (gs.granted) parts.push(`открыт доступ: ${gs.granted}`);
        if (gs.held) parts.push(`доступ приостановлен: ${gs.held}`);
        toast.show(`Тариф сохранён. ${parts.join(', ')}`, 'success', 8000);
      } else {
        toast.show('Тариф сохранён', 'success');
      }
      reload();
    } catch (e) {
      const msg = e?.detail || e?.message || 'Ошибка';
      setFieldErr({ [e?.field || '_']: msg });
      // Форма не сохранилась целиком — значит на сервере тариф остался прежним.
      // Галочка «Активен» при этом оставалась включённой, и методист уходил с
      // экрана уверенным, что тариф продаётся, хотя он выключен. Возвращаем
      // ПРАВДУ сервера (реквизит `tariff` при отказе не перечитывался).
      setT((s) => ({ ...s, is_active: tariff.is_active,
        homework_enabled: tariff.homework_enabled !== false,
        show_locked_modules: tariff.show_locked_modules !== false,
        grant_by_subscription: !!tariff.grant_by_subscription }));
      toast.show(msg, 'danger');
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!await confirm({ title: 'Удалить тариф?', confirmLabel: 'Удалить', danger: true })) return;
    try { await api.deleteTariff(tariff.id); reload(); return; }
    catch (e) {
      // Купленный тариф и тариф с записями удалить нечем: на него ссылаются
      // платёжная запись и доступ ученика (PROTECT). Раньше методист читал это в
      // тосте и шёл искать чекбокс «Активен» сам — теперь деактивация здесь же.
      if (['tariff_purchased', 'has_enrollments', 'protected'].includes(e?.code)) {
        if (!tariff.is_active) {
          // Уже выключен — предлагать «деактивировать» значит предлагать кнопку,
          // которая ничего не изменит.
          toast.show((e?.detail || 'Тариф удалить нельзя') + ' Он уже не продаётся.', 'warning');
          return;
        }
        const ok = await confirm({ title: 'Удалить нельзя — деактивировать?',
          body: (e?.detail || 'На тариф ссылаются покупки или доступы учеников.')
            + ' Деактивация уберёт тариф из продажи; у тех, кто уже учится, доступ сохранится.',
          confirmLabel: 'Деактивировать' });
        if (!ok) return;
        try {
          await api.updateTariff(tariff.id, { is_active: false });
          // Локальное состояние карточки НЕ пересинхронизируется само (иначе
          // затирало бы несохранённую правку методиста), а `key` при reload не
          // меняется — компонент не перемонтируется. Без этой строки экран
          // показывал зелёный тост «снят с продажи» и рядом отмеченную галочку
          // «Активен», и следующее «Сохранить» вернуло бы тариф в продажу.
          // Тот же приём, что в `save()` при отказе: печатаем правду сервера.
          setT((s) => ({ ...s, is_active: false }));
          toast.show('Тариф снят с продажи', 'success'); reload();
        } catch (e2) { toast.show(e2?.detail || e2?.message || 'Не удалось деактивировать', 'danger'); }
        return;
      }
      toast.show(e?.detail || e?.message || 'Нельзя удалить — есть записи. Деактивируйте.', 'danger');
    }
  };

  return (
    <div className="acad-tariff-card">
      <div className="acad-grid-form">
        <Field label="Название тарифа"><TextInput value={t.title} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="Цена, ₽" error={fieldErr.price_rub}
          hint={t.grant_by_subscription
            ? 'тариф-пропуск не продаётся — цена не используется'
            : 'активный тариф продаётся — цена должна быть больше 0'}>
          <input className={'input' + (fieldErr.price_rub ? ' input--error' : '')} type="number" min="0"
            value={t.price_rub} onChange={(e) => set('price_rub', e.target.value)} /></Field>
      </div>
      <div className="acad-grid-form">
        <Field label="Доступ к урокам, дней" hint="пусто — пожизненно"><input className="input" type="number" min="0" value={t.access_days} onChange={(e) => set('access_days', e.target.value)} /></Field>
        <Field label="Обратная связь, дней" hint="пусто — как доступ"><input className="input" type="number" min="0" value={t.feedback_days} onChange={(e) => set('feedback_days', e.target.value)} /></Field>
      </div>
      {/* AD-9. Окно ОС на тарифе «Проверяет куратор» — это ещё и срок ПРИЁМА работ
          (services.feedback_closed): после него ученик не сдаст ДЗ вовсе, а значит
          не закроет обязательный урок и не завершит курс. Методист ставил окно,
          считая, что ограничивает только разбор работ. Для «автоприёма» и
          «куратор, затем автоматически» этой ловушки нет — там ДЗ засчитывается. */}
      {t.homework_review_mode === 'curator' && String(t.feedback_days).trim() !== '' && (
        <div className="acad-notice acad-notice--warn" role="status">
          <Icon name="alert-triangle" size={15} />
          <span>После {t.feedback_days} дн. ученик этого тарифа НЕ сможет сдать ДЗ —
            окно обратной связи закрывает и приём работ. Обязательные уроки с ДЗ
            останутся незакрытыми, курс не будет завершён и сертификат не выдастся.
            Если нужен только предел разбора работ, выберите «Куратор пока идёт ОС,
            затем автоприём».</span>
        </div>
      )}
      <Checkbox label="Домашние задания" checked={t.homework_enabled}
        onChange={(e) => set('homework_enabled', e.target.checked)} />
      {!t.homework_enabled && (
        <div className="acad-notice" style={{ marginLeft: 'var(--space-8)' }}>
          <Icon name="info" size={15} />
          <span>Тариф «без ДЗ»: задания не показываются ученику и не требуются
            для прохождения урока и получения сертификата.</span>
        </div>
      )}
      {/* Режим приёма прячем целиком: задавать проверку несуществующих работ
          бессмысленно, а оставленный активным селект читается как «ДЗ всё-таки есть». */}
      {t.homework_enabled && (
        <Field label="Приём ДЗ" hint="как засчитываются домашние задания этого тарифа">
          <NativeSelect value={t.homework_review_mode} onChange={(e) => set('homework_review_mode', e.target.value)}
            options={HW_MODES} />
        </Field>
      )}
      <Checkbox label="Все модули курса" checked={t.includes_all_modules} onChange={(e) => set('includes_all_modules', e.target.checked)} />
      {!t.includes_all_modules && (
        <div className="stack stack-2" style={{ paddingLeft: 'var(--space-8)' }}>
          <span className="t-caption text-dim">Модули в тарифе:</span>
          {modules.map((m) => (
            <Checkbox key={m.id} label={m.title} checked={t.module_ids.includes(m.id)} onChange={() => toggleModule(m.id)} />
          ))}
          <Checkbox label="Показывать модули вне тарифа" checked={t.show_locked_modules}
            onChange={(e) => set('show_locked_modules', e.target.checked)} />
          <span className="t-caption text-dim">
            {t.show_locked_modules
              ? 'Ученик видит остальные модули с замком «Не входит в ваш тариф».'
              : 'Остальные модули ученику не показываются совсем.'}
          </span>
        </div>
      )}
      <Checkbox label="Выдавать сертификат" checked={t.grants_certificate} onChange={(e) => set('grants_certificate', e.target.checked)} />
      <Checkbox label="Выдавать по подписке Restorium" checked={t.grant_by_subscription}
        onChange={(e) => toggleGrant(e.target.checked)} />
      {t.grant_by_subscription && (
        <div className="stack stack-3" style={{ paddingLeft: 'var(--space-8)' }}>
          <div className="acad-notice">
            <Icon name="info" size={15} />
            <span>Тариф-пропуск: он не продаётся и не показывается в витрине курса.
              Доступ открывается сам, пока у пространства активна подходящая подписка,
              и встаёт на паузу, когда она заканчивается — прогресс при этом сохраняется.</span>
          </div>
          <Field label="Кому выдавать" hint="аудиторное правило ловит и будущие планы этой группы">
            <NativeSelect value={t.granted_to_audience}
              onChange={(e) => setT((s) => ({ ...s,
                granted_to_audience: e.target.value,
                grant_reach: e.target.value === 'personal' ? 'owner' : s.grant_reach }))}
              options={GRANT_AUDIENCES} />
          </Field>
          {/* Аудиторное правило: конкретного плана в нём нет, поэтому охват один
              на всё правило. В поимённом режиме он не читается — там у каждого
              плана свой, и показывать оба переключателя разом значит спрашивать
              одно и то же дважды. */}
          {t.granted_to_audience === 'personal' && (
            <span className="t-caption text-dim">
              Личные планы: доступ получает владелец пространства — он же подписчик.
            </span>
          )}
          {t.granted_to_audience && t.granted_to_audience !== 'personal' ? (
            <Field label="Кому в бизнес-пространстве"
              hint="личных пространств это не касается: там доступ всегда у владельца">
              <NativeSelect value={t.grant_reach} onChange={(e) => set('grant_reach', e.target.value)}
                options={GRANT_REACHES} />
            </Field>
          ) : t.granted_to_audience ? null : (
            <div className="stack stack-2">
              <div className="cluster cluster-2">
                <span className="t-caption text-dim grow">
                  Планы подписки и кому в них открывать курс:
                </span>
                <Button size="sm" variant="ghost" onClick={allPlans}>Выбрать все</Button>
                <Button size="sm" variant="ghost" onClick={noPlans}>Убрать все</Button>
              </div>
              {plans.length === 0
                ? <span className="t-caption text-dim">Планы подписки не загрузились.</span>
                : plans.map((p) => {
                  const on = t.granted_plans.some((r) => r.slug === p.slug);
                  return (
                    <div key={p.slug} className="cluster cluster-2">
                      <Checkbox className="grow"
                        label={p.name + ' · ' + p.slug + (p.is_private ? ' · закрытый' : '')
                          + (p.audience === 'business' ? ' · бизнес' : ' · личный')}
                        checked={on} onChange={() => togglePlan(p.slug)} />
                      {/* Личное пространство: подписчик и владелец — одно лицо,
                          выбирать нечего, поэтому переключатель только у бизнеса. */}
                      {on && p.audience === 'business' && (
                        <NativeSelect value={planReach(p.slug) || 'owner'}
                          onChange={(e) => setPlanReach(p.slug, e.target.value)}
                          options={GRANT_REACHES} />
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
      <Checkbox label="Доступен для записи" checked={t.is_active} onChange={(e) => set('is_active', e.target.checked)} />
      {(fieldErr._ || fieldErr.is_active) && (
        <div className="acad-notice acad-notice--warn" role="alert">
          <Icon name="alert-triangle" size={15} /><span>{fieldErr._ || fieldErr.is_active}</span>
        </div>
      )}
      <div className="cluster cluster-8" style={{ justifyContent: 'space-between' }}>
        <Button variant="primary" size="sm" loading={busy} onClick={save}>Сохранить</Button>
        <Button variant="ghost" size="sm" iconLeft="trash" onClick={del}>Удалить</Button>
      </div>
    </div>
  );
}

// #14: тарифы тянем ОТДЕЛЬНЫМ запросом (listTariffs) и обновляем ТОЛЬКО их после CRUD —
// без перезапроса всего курса (модули/уроки/ученики), который раньше подвешивал UI на 3-м тарифе.
// Модули для выбора в тарифе берём из course-пропа (на CRUD тарифа они не меняются).
// AD-5: после КАЖДОГО изменения тарифа курс всё же перезапрашивается (reloadCourse) —
// иначе новый тариф не виден в выпадашке «Ученики» до закрытия дровера.
function TariffsTab({ course, reload }) {
  const toast = useToast();
  const [nonce, setNonce] = useState(0);
  const q = useFakeQuery('academy-tariffs-' + course.id + '-' + nonce,
    () => api.listTariffs(course.id), [course.id, nonce]);
  // Один запрос на вкладку, а не по одному на карточку: тарифов у курса бывает
  // несколько, и список планов у них общий.
  const plansQ = useFakeQuery('academy-sub-plans', () => api.listSubscriptionPlans(), []);
  const plans = plansQ.data || [];
  const tariffs = q.data || [];
  const hasActive = tariffs.some((t) => t.is_active && !t.grant_by_subscription);
  const afterChange = () => { setNonce((n) => n + 1); reload(); };
  const add = async () => {
    // Новый тариф создаётся НЕАКТИВНЫМ: активный тариф с ценой 0 мгновенно ломает
    // покупку всего курса для всех покупателей (_resolve_tariff → bad_price).
    try {
      await api.createTariff(course.id, { title: 'Новый тариф', price_rub: '0',
        includes_all_modules: true, is_active: false });
      toast.show('Тариф добавлен (неактивен) — задайте цену и включите продажу', 'success');
      afterChange();
    } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  return (
    <div className="stack stack-4">
      <div className="t-caption text-dim">Тарифы — разные комплектации одного курса (набор модулей, проверка ДЗ, цена, сроки). Без тарифов курс продаётся по одной цене из «Настроек».</div>
      {hasActive && (
        <div className="acad-notice acad-notice--warn" role="status">
          <Icon name="alert-triangle" size={15} />
          <span>Курс продаётся по тарифам: покупка по цене курса отключена. Покупатель
            выбирает один из активных тарифов — цена из «Настроек» не применяется.</span>
        </div>
      )}
      <QueryBoundary loading={q.loading && !q.data} error={q.error} onRetry={afterChange}>
        {!tariffs.length
          ? (<div className="acad-block-empty"><Icon name="tag" size={28} />
              <div className="acad-block-empty__title">Тарифов пока нет</div>
              <span className="t-caption">Добавьте тариф, чтобы продавать курс разными комплектациями.</span></div>)
          : tariffs.map((t) => <TariffCard key={t.id} tariff={t} modules={course.modules || []}
              plans={plans} reload={afterChange} />)}
      </QueryBoundary>
      <div><Button variant="secondary" iconLeft="plus" onClick={add}>Добавить тариф</Button></div>
    </div>
  );
}

function CourseEditor({ courseId, onClose, canManage = true, courseTitle }) {
  const [nonce, setNonce] = useState(0);
  // Вкладки — только те, что роль правда откроет. Куратору остаётся аналитика.
  const tabs = api.courseEditorTabs(canManage);
  const [rawTab, setTab] = useState(tabs[0].id);
  // Страховка от «висящей» вкладки: если набор прав изменился под открытым
  // дровером, активной остаётся доступная, а не та, что покажет пустое тело.
  const tab = tabs.some((t) => t.id === rawTab) ? rawTab : tabs[0].id;
  const [lessonId, setLessonId] = useState(null);
  /* Дерево курса лежит за `manage/courses/<id>/` (IsAcademyManager). У куратора
     этот запрос гарантированно вернёт 403, поэтому его не делаем вовсе: раньше
     дровер рисовал полную шапку и «Нет доступа» в теле — обещание шести рабочих
     мест и отказ на каждом. Аналитику куратор запрашивает сам (AnalyticsTab). */
  const q = useFakeQuery('academy-course-' + courseId + '-' + (canManage ? 1 : 0) + '-' + nonce,
    () => (canManage ? api.getCourse(courseId) : Promise.resolve(null)), [courseId, canManage, nonce]);
  const course = q.data;
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const impact = useCourseImpact(course, nonce);
  const guard = useRequirementGuard(impact);

  return (
    <>
      <Drawer open onClose={() => onClose(true)} size="lg"
        title={course?.title || courseTitle || (canManage ? 'Курс' : 'Аналитика курса')}
        breadcrumbs={course
          ? `${course.lesson_count} ${api.lessonsWord(course.lesson_count)}`
            + ` · ${course.enrollment_count} ${api.studentsWord(course.enrollment_count)}`
          : null}>
        {/* .acad-pane — контейнер для container-query (PL-15): вёрстка консоли
            подстраивается под ширину ПАНЕЛИ дровера, а не вьюпорта. */}
        <div className="acad-pane">
          {course && (
            <div className={'acad-livebar' + (impact.live ? ' acad-livebar--live' : '')}>
              <Icon name={impact.live ? 'alert-triangle' : 'info'} size={14} />
              {impact.live ? (
                <span>
                  {/* «—» вместо «…», когда аналитика ответила ошибкой: бесконечное
                      многоточие читалось как «ещё грузится» и скрывало отказ. */}
                  Опубликован · <span className="acad-livebar__num">
                    {impact.ready ? impact.active : (impact.failed ? '—' : '…')}</span>
                  {' '}активных учеников · правки применяются к ним сразу
                </span>
              ) : course.status === 'published' ? (
                <span>Опубликован · учеников пока нет</span>
              ) : (
                <span>{STATUS_LABEL[course.status] || course.status} · курс не виден ученикам</span>
              )}
            </div>
          )}
          {/* Шесть вкладок с иконками не влезают в панель дровера на 380 — полоса
              скроллится, и без затухания у края методист не видел, что справа есть
              «Ученики» и «Аналитика». Полосу рисуем, только если вкладок больше
              одной: куратору с единственной «Аналитикой» таб-бар из одной кнопки
              ничего не сообщает. */}
          {tabs.length > 1 && (
            <TabScroller>
              <div className="acad-tabs" role="tablist" style={{ marginBottom: 'var(--space-16)' }}>
                {tabs.map((t) => (
                  <button key={t.id} role="tab" aria-selected={tab === t.id}
                    className={'acad-seg' + (tab === t.id ? ' is-active' : '')} onClick={() => setTab(t.id)}>
                    <Icon name={t.icon} size={14} />{t.label}
                  </button>
                ))}
              </div>
            </TabScroller>
          )}
          <QueryBoundary loading={q.loading && !course} error={q.error} onRetry={reload}>
            {course && tab === 'structure' && <StructureTab course={course} reload={reload} openLesson={setLessonId} guard={guard} />}
            {course && tab === 'settings' && <SettingsTab course={course} reload={reload} onClose={onClose} />}
            {course && tab === 'landing' && <LandingTab course={course} reload={reload} />}
            {course && tab === 'tariffs' && <TariffsTab course={course} reload={reload} />}
            {course && tab === 'students' && <StudentsTab course={course} reloadCourse={reload} />}
            {/* Аналитике хватает id: она ходит своим запросом и не читает дерево
                курса — поэтому она же и работает у куратора, которому дерево закрыто. */}
            {tab === 'analytics' && <AnalyticsTab course={course || { id: courseId }} />}
          </QueryBoundary>
        </div>
      </Drawer>

      {lessonId && <LessonEditor lessonId={lessonId} courseId={courseId} courseTitle={course?.title}
        modules={course?.modules || []} guard={guard}
        onClose={(changed) => { setLessonId(null); if (changed) reload(); }} />}
    </>
  );
}

export { CourseEditor };
